import React, { useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { flattenToAppURL } from '@plone/volto/helpers';

import moment from 'moment';

import Card from 'react-bootstrap/Card';

import { ImArrowDownRight2 } from 'react-icons/im';

import PreviewImagePT from '../../Utils/PreviewImage/PreviewImagePT';

import './less/ImageWithTextBelow.less';

const descriptionSource = (description) => {
  if (description == null) {
    return '';
  }
  if (typeof description === 'string') {
    return description;
  }
  if (typeof description === 'object' && description.data != null) {
    return String(description.data);
  }
  return '';
};

const SLIDER_DESC_LINES_MIN = 3;
const SLIDER_TITLE_DESC_GAP_PX = 6;
/** Només passem de 3 línies si hi ha ~mig línia útil per sobre del bloc de 3. */
const SLIDER_EXTRA_DESC_SLACK_LH = 0.45;
/** Petit marge reductible al comptar línies per no inflar el bloc. */
const SLIDER_DESC_MEASURE_TRIM_LH = 0.2;

const descLineHeightPx = (el) => {
  if (!el || typeof window === 'undefined') {
    return 21.6;
  }
  const st = window.getComputedStyle(el);
  const fs = parseFloat(st.fontSize) || 16;
  const lh = st.lineHeight;
  if (lh === 'normal') {
    return fs * 1.35;
  }
  const n = parseFloat(lh);
  return Number.isFinite(n) && n > 0 ? n : fs * 1.35;
};

const ImageWithTextBelow = ({
  item,
  addAudiences = false,
  /** Al slider: màxim de línies si el títol deixa força espai (per defecte 3 + fins aquest sostre). */
  descriptionMaxLines = 5,
  newsSliderLayout = false,
}) => {
  const descriptionHtml = descriptionSource(item?.description).trim();
  const showDescPlaceholder = newsSliderLayout && !descriptionHtml;

  const cardRef = useRef(null);
  const metaRef = useRef(null);
  const titleRef = useRef(null);
  const descRef = useRef(null);

  const lineCap = Math.max(SLIDER_DESC_LINES_MIN, descriptionMaxLines);
  const [sliderDescLines, setSliderDescLines] = useState(() =>
    newsSliderLayout ? SLIDER_DESC_LINES_MIN : lineCap,
  );

  useLayoutEffect(() => {
    if (!newsSliderLayout) {
      return;
    }

    const card = cardRef.current;
    if (!card) {
      return;
    }

    const measure = () => {
      const headerEl = card.querySelector('.card-header');
      const bodyEl = card.querySelector('.card-body');
      const footerEl = card.querySelector('.card-footer');
      const metaEl = metaRef.current;
      const titleEl = titleRef.current;

      if (!bodyEl || !titleEl) {
        return;
      }

      const cardH = card.getBoundingClientRect().height;
      if (cardH <= 0) {
        return;
      }

      const headerH = headerEl ? headerEl.getBoundingClientRect().height : 0;
      const footerH = footerEl ? footerEl.getBoundingClientRect().height : 0;
      const bodyStyle = window.getComputedStyle(bodyEl);
      const bodyPadT = parseFloat(bodyStyle.paddingTop) || 0;
      const bodyPadB = parseFloat(bodyStyle.paddingBottom) || 0;

      const metaH = metaEl ? metaEl.getBoundingClientRect().height : 0;
      const titleH = titleEl.getBoundingClientRect().height;

      const used =
        headerH +
        bodyPadT +
        metaH +
        titleH +
        SLIDER_TITLE_DESC_GAP_PX +
        bodyPadB +
        footerH;

      const avail = cardH - used - 2;
      const lh = descLineHeightPx(descRef.current || bodyEl);
      const spaceForMinDesc = SLIDER_DESC_LINES_MIN * lh;

      let n = SLIDER_DESC_LINES_MIN;
      if (avail > spaceForMinDesc + SLIDER_EXTRA_DESC_SLACK_LH * lh) {
        n = Math.floor(
          (avail - SLIDER_DESC_MEASURE_TRIM_LH * lh) / lh,
        );
        n = Math.max(SLIDER_DESC_LINES_MIN, Math.min(n, lineCap));
      }

      setSliderDescLines(n);
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(card);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [newsSliderLayout, lineCap, item?.title, descriptionHtml, item?.image]);

  const cardClass = [
    'mb-3 w-100 image-with-text-below',
    newsSliderLayout ? 'image-with-text-below--news-slider' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const resolvedSliderLines = newsSliderLayout ? sliderDescLines : descriptionMaxLines;

  return (
    <Card ref={newsSliderLayout ? cardRef : undefined} className={cardClass}>
      {item.image && (
        <Card.Header>
          <Link
            className="text-block-moreinfo"
            to={flattenToAppURL(item['@id'])}
          >
            <PreviewImagePT
              item={item}
              alt={item.image_alt || item.title}
              imageField="image"
              className="ui image rounded-4 w-100"
            />
          </Link>
        </Card.Header>
      )}
      <Card.Body>
        <div
          ref={newsSliderLayout ? metaRef : undefined}
          className={`image-with-text-below__meta d-flex flex-wrap align-items-center row-gap-2 ${newsSliderLayout ? 'mb-2' : 'mb-3'}`}
        >
          {addAudiences &&
            item.audience &&
            item.audience.length > 0 &&
            item.audience.map((aud) => (
              <p className="py-1 px-3 me-2 mb-0 border border-dark rounded-pill">
                {aud.title}
              </p>
            ))}
          <p
            className={`mb-0 ${newsSliderLayout ? 'small text-muted' : ''}`}
          >
            {moment(item.effective).format('D MMMM YYYY')}
          </p>
        </div>
        <Card.Title className="mt-0 pb-2 border-bottom">
          <Link
            to={flattenToAppURL(item['@id'])}
            className="d-block text-decoration-none text-body"
          >
            <span
              ref={newsSliderLayout ? titleRef : undefined}
              className="d-block"
            >
              {item?.title}
            </span>
          </Link>
        </Card.Title>
        {descriptionHtml ? (
          <Card.Text
            ref={newsSliderLayout ? descRef : undefined}
            as="div"
            className={
              newsSliderLayout
                ? 'image-with-text-below__desc image-with-text-below__desc--slider text-truncate-x text-body-secondary mb-0'
                : 'image-with-text-below__desc text-body-secondary mb-0'
            }
            style={
              newsSliderLayout
                ? { '--text-lines': resolvedSliderLines }
                : undefined
            }
            dangerouslySetInnerHTML={{ __html: descriptionHtml }}
          />
        ) : showDescPlaceholder ? (
          <div
            className="image-with-text-below__desc image-with-text-below__desc--empty text-body-secondary mb-0"
            style={{ '--text-lines': resolvedSliderLines }}
            aria-hidden="true"
          />
        ) : null}
      </Card.Body>
      <Card.Footer>
        <Link className="text-block-moreinfo" to={flattenToAppURL(item['@id'])}>
          <ImArrowDownRight2 className="me-2" aria-hidden="true" /> More Info
        </Link>
      </Card.Footer>
    </Card>
  );
};

export default ImageWithTextBelow;
